
import { Component, Vue, Prop } from 'vue-property-decorator';
import dateFormat from 'dateformat';
import { StoreModel } from '@/models/store';
import moment from 'moment';
import ActionButton from "@/components/ActionButton.vue";
import { EventBus } from "@/main";
import { FunnelEventModel } from '@/models/funnelEvent';
import { OnboardingFeedbackEventHandler } from '@/modules/onboardingFeedbackEvents';

@Component({
  components: {
    ActionButton,
  },
})
export default class OnboardCompleteConfirm extends Vue {
  @Prop({ default: () => new StoreModel() })
  public store!: StoreModel;
  public confirmRightReport: boolean = false;
  public confirmCorrectData: boolean = false;
  public topReports: string = '';
  public canAskToday: boolean = false;
  public dialogConfirmRightReport: boolean = false;
  public dialogConfirmCorrectData: boolean = false;
  public dialogConfirmRedirect: boolean = false;
  public dialogUpdateTopReport: boolean = false;
  public dialogWrongDataExample: boolean = false;
  public dialogDataNotCorrect: boolean = false;
  public updateTopReportsMessage: string = '';
  public saveSettingLoading: boolean = false;
  public wrongDataExample: string = '';
  public saveWrongDataConfirmLoading: boolean = false;
  public saveCorrectDataConfirmLoading: boolean = false;
  @Prop({ default: 'dialog' })
  public type!: string;
  private feedbackTracker!: OnboardingFeedbackEventHandler;

  public created() {
    // Initialize feedback tracker
    this.feedbackTracker = new OnboardingFeedbackEventHandler();

    this.initData();
    this.checkCanAskToday();
    if (this.type === 'dialog') {
      this.showDialogConfirmRightReport();
      this.showDialogConfirmCorrectData();
    }

    // Track dialog viewed
    if (this.canAskToday) {
      this.feedbackTracker.trackDialogViewed(this.type as 'dialog' | 'appBanner');
    }
  }

  public initData() {
    if (!this.store.config) {
      return;
    }
    if (this.store.config.topReports) {
      this.topReports = this.store.config.topReports || '';
    }
    if (this.store.config.confirmCorrectData === 'true') {
      this.confirmCorrectData = true;
    }
    if (this.store.config.confirmRightReport === 'true') {
      this.confirmRightReport = true;
    }
  }

  public checkCanAskToday() {
    if (this.type !== 'dialog') {
      this.canAskToday = true;
      return true;
    }
    if (this.store.currentPackage.name === 'Free') {
      this.canAskToday = false;
      return;
    }
    const now = moment();
    //get lastAskedDate from local storage
    let lastAskedDateStr: string = localStorage.getItem('lastAskedDate') || '';
    if (lastAskedDateStr !== '' && lastAskedDateStr !== null) {
      const lastAskedDate = moment(lastAskedDateStr);
      if (!lastAskedDate.isSame(now, 'day')) {
        this.canAskToday = true;
      } else {
        this.canAskToday = false;
      }
    } else {
      this.canAskToday = true;
    }
    //save the lastAskedDate to local storage
    localStorage.setItem('lastAskedDate', now.format());
  }

  public showDialogConfirmRightReport() {
    if (!this.confirmRightReport && this.canAskToday) {
      this.dialogConfirmRightReport = true;
    }
  }

  public showDialogConfirmCorrectData() {
    if (!this.confirmCorrectData && this.canAskToday && this.confirmRightReport) {
      this.dialogConfirmCorrectData = true;
    }
  }

  public showUpdateTopReport() {
    this.dialogUpdateTopReport = true;
  }

  public doNotSendTopReport() {
    this.dialogUpdateTopReport = false;
    this.openChatConfirmRightReport();
  }

  public sendTopReport() {
    this.dialogUpdateTopReport = false;
    this.updateSetting();
    this.openChatConfirmRightReport(true);
  }
  public async updateSetting() {
    this.store.config.topReports = this.topReports;
    this.store.config.confirmCorrectData = this.confirmCorrectData;
    this.store.config.confirmRightReport = this.confirmRightReport;
    try {
      await this.store.saveSettings();
      EventBus.$emit('show-snackbar', {
        message: 'Thanks for updating your preferences!',
        color: 'success',
      });
    } catch (error: any) {
      EventBus.$emit('show-snackbar', {
        message: 'Error updating your preferences!',
        color: 'error',
      });
    }
  }

  public confirmRightReportAction(value: boolean) {
    this.confirmRightReport = value;
    this.dialogConfirmRightReport = false;
    this.updateSetting();

    if (value) {
      // User found the right report
      this.updateTopReportsMessage = 'To better understand your needs and improve our service, please update your top reports';

      // Track YES response
      this.feedbackTracker.trackRightReportResponse(true);

      try {
        const funnelEvent = new FunnelEventModel({
          name: 'CONFIRMED_CORRECT_REPORT',
        });
        funnelEvent.create();
      } catch (error) {
        // skipped
      }

      // Show update top reports dialog
      this.dialogUpdateTopReport = true;
    } else {
      // User did NOT find the right report

      // Track NO response
      this.feedbackTracker.trackRightReportResponse(false);

      try {
        const funnelEvent = new FunnelEventModel({
          name: 'CONFIRMED_INCORRECT_REPORT',
        });
        funnelEvent.create();
      } catch (error) {
        // skipped
      }

      // Show confirmation dialog asking if user wants to redirect now or later
      this.dialogConfirmRedirect = true;
    }
  }

  public async redirectNow() {
    this.dialogConfirmRedirect = false;

    // Track redirect and navigate to AI Report Builder
    this.feedbackTracker.trackAIBuilderRedirect();

    try {
      await this.$router.push('/ai-report-builder');

      // Track feedback completed (only if navigation succeeds)
      this.feedbackTracker.trackFeedbackCompleted();
    } catch (error: any) {
      // Navigation failed - reset dialog so user can retry
      this.dialogConfirmRedirect = true;

      // Show user-facing error message
      EventBus.$emit('show-snackbar', {
        message: 'Unable to navigate to AI Report Builder. Please try again.',
        color: 'error',
      });

      // Log the navigation error for debugging
      console.error('[OnboardCompleteConfirm] Navigation to AI Report Builder failed:', error);

      // Track navigation failure for analytics
      try {
        this.feedbackTracker.track('Onboarding Feedback - Navigation Error', {
          sessionId: this.feedbackTracker.getSessionState().sessionId,
          targetRoute: '/ai-report-builder',
          errorMessage: error?.message || 'Unknown navigation error',
          timestamp: new Date().toISOString(),
        });
      } catch (trackingError) {
        // Silently fail tracking to avoid double errors
        console.error('[OnboardCompleteConfirm] Failed to track navigation error:', trackingError);
      }
    }
  }

  public redirectLater() {
    this.dialogConfirmRedirect = false;

    // Track that user chose to redirect later
    this.feedbackTracker.trackRedirectLater();

    // Track feedback completed
    this.feedbackTracker.trackFeedbackCompleted();
  }

  public confirmCorrectDataAction(value: boolean) {
    this.confirmCorrectData = value;

    // Track correct data response
    this.feedbackTracker.trackCorrectDataResponse(value);

    if (value) {
      this.saveCorrectDataConfirmLoading = true;
      try {
        const funnelEvent = new FunnelEventModel({
          name: 'CONFIRMED_CORRECT_DATA',
        });
        funnelEvent.create();
      } catch (error) {
        // skipped
      }
    } else {
      this.saveWrongDataConfirmLoading = true;
      try {
        const funnelEvent = new FunnelEventModel({
          name: 'CONFIRMED_INCORRECT_DATA',
        });
        funnelEvent.create();
      } catch (error) {
        // skipped
      }
    }
    this.updateSetting();
    if (!value) {
      this.dialogWrongDataExample = true;
    }
    this.dialogConfirmCorrectData = false;
    if (value) {
      this.saveCorrectDataConfirmLoading = false;

      // Track feedback completed (data confirmed as correct)
      this.feedbackTracker.trackFeedbackCompleted();
    } else {
      this.saveWrongDataConfirmLoading = false;
    }
  }

  public openChatConfirmRightReport(sendTopReport: boolean = false) {
    let message: string = '';
    if (sendTopReport) {
      message = 'Please help me to set up the right reports: ' + this.topReports;
    } else {
      if (this.confirmRightReport) {
        message = 'I got the right report, please help me to verify the data.';
      } else {
        message = 'I need help to find the right report.';
      }
    }

    // Track support chat opened
    this.feedbackTracker.trackSupportChatOpened('other', message);

    // Track feedback completed (after chat state is recorded)
    this.feedbackTracker.trackFeedbackCompleted();

    // @ts-ignore: Unreachable code error
    this.$crisp.do('chat:open');
      // @ts-ignore: Unreachable code error
    this.$crisp.do('message:send', ['text', message]);
  }

  public openChatWrongDataExample(sendExample: boolean = false) {
    this.dialogWrongDataExample = false;
    let message: string = '';
    if (sendExample) {
      message = 'Wrong data example: ' + this.wrongDataExample;
    } else {
      if (this.confirmCorrectData) {
        message = 'I confirm that the data provided by Assisty is correct!';
      } else {
        message = 'I need help to correct the data!';
      }
    }

    // Track support chat opened
    this.feedbackTracker.trackSupportChatOpened('wrong_data', message);

    // Track feedback completed (user shared wrong data example or asked for help)
    this.feedbackTracker.trackFeedbackCompleted();

    // @ts-ignore: Unreachable code error
    this.$crisp.do('chat:open');
      // @ts-ignore: Unreachable code error
    this.$crisp.do('message:send', ['text', message]);
  }
}
